import { Component, Input, OnInit, forwardRef, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';

import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject, take, takeUntil, forkJoin } from 'rxjs';
import { RenewalUser, Direction, ServiceFonctionnel } from '@models/domains/renewal';
import { Pole, PosteOccupe, TypeContrat } from '@models/shared';
import { SituationAdministrativeFormValue, DateValue } from '@models/api/requests';
import { AgentsService } from 'src/app/core/services/agents.service';
import { ReferentielsService } from 'src/app/core/services/referentiels.service';
import { DirectionService } from 'src/app/core/services/direction.service';
import { FonctionnelService } from 'src/app/core/services/fonctionnel.service';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomLuxonDateAdapter } from '../../utils/custom-luxon-date-adapter';
import { MY_FORMATS } from '../../utils/date-pickr-custom-luxon-format';
import { MatDividerModule } from '@angular/material/divider';
import { DateTime } from 'luxon';

@Component({
    selector: 'app-situation-administrative-form',
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatDividerModule
],
    templateUrl: './situation-administrative-form.component.html',
    styleUrls: ['./situation-administrative-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SituationAdministrativeFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SituationAdministrativeFormComponent),
            multi: true
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'fr-FR'
        },
        { provide: DateAdapter, useClass: CustomLuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ]
})
export class SituationAdministrativeFormComponent implements OnInit, ControlValueAccessor, OnDestroy, Validator {
  @Input() readOnly = false;
  @Input() demandeId: string | null = null;
  @Output() agentSelected = new EventEmitter<any>();

  form: FormGroup;

  // Lists for dropdowns
  poleList: Pole[] = [];
  directionList: Direction[] = [];
  serviceFonctionnelList: ServiceFonctionnel[] = [];
  posteList: PosteOccupe[] = [];
  typeContratList: TypeContrat[] = [];
  categoriePosteList: any[] = [];

  // Type renouvellement related properties
  recrutementList: any[] = [];
  recrutementListFiltered: any[] = [];
  agentsList: RenewalUser[] = [];
  private readonly destroy$ = new Subject<void>();
  private onChange: (value: SituationAdministrativeFormValue) => void = () => {};
  private onTouched: () => void = () => {};
  dateExpirationCS: any = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly agentsService: AgentsService,
    private readonly referentielsService: ReferentielsService,
    private readonly directionService: DirectionService,
    private readonly fonctionnelService: FonctionnelService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // Load initial data
    this.loadData();

    // Subscribe to form value changes to notify parent
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onChange(this.form.getRawValue());
      this.onTouched();
    });

    // Subscribe to agent changes to emit the agent for cdisation data
    this.form
      .get('agent')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((agent) => {
        if (agent) {
          this.agentSelected.emit(agent);
        }
      });

    // Subscribe to pole changes to load directions
    this.form
      .get('pole')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((pole) => {
        if (pole) {
          this.loadDirectionList(pole.id);
        }
      });

    // Subscribe to direction changes to load services
    this.form
      .get('direction')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((direction) => {
        if (direction) {
          this.loadServiceList(direction.id);
        }
      });
  }

  initializeForm(): void {
    this.form = this.fb.group({
      agent: [{ value: null, disabled: true }, [Validators.required]],
      categoriePoste: [{ value: null, disabled: true }],
      identifiantAgent: [{ value: '', disabled: true }, [Validators.required]],
      grade: [{ value: '', disabled: true }, [Validators.required]],
      dateEntree: [{ value: '', disabled: this.readOnly }, [Validators.required]],
      posteOccupe: [{ value: null, disabled: this.readOnly }, [Validators.required]],
      tauxEmploi: [{ value: '', disabled: this.readOnly }, [Validators.required]],
      fonction: [{ value: '', disabled: true }, [Validators.required]],
      pole: [{ value: null, disabled: this.readOnly }, [Validators.required]],
      direction: [{ value: null, disabled: this.readOnly }, [Validators.required]],
      serviceFonctionnel: [{ value: null, disabled: this.readOnly }, [Validators.required]],
      typeContrat: [{ value: null, disabled: this.readOnly }, [Validators.required]],
      typeRenouvellement: [{ value: '', disabled: this.readOnly }, [Validators.required]],
      agentRemplace: [{ value: true, disabled: this.readOnly }, [Validators.required]],
      dateRange: this.fb.group({
        start: [{ value: '', disabled: this.readOnly }, [Validators.required]],
        end: [{ value: '', disabled: this.readOnly }, [Validators.required]]
      })
    });
  }

  // ControlValueAccessor methods
  writeValue(value: SituationAdministrativeFormValue): void {
    if (value) {
      // Use patchValue to update the form, including disabled controls
      this.form.patchValue(value, { emitEvent: false });

      this.agentsList = value.agent ? [value.agent] : [];

      // Emit the agent data for cdisation if available
      if (value.agent) {
        this.agentSelected.emit(value.agent);

        // rempli la date d'expiration de CS, ce champ est en lecture seule
        let dateECS = value.agent.dateExpirationCS ? DateTime.fromISO(value.agent.dateExpirationCS) : null;
        this.dateExpirationCS = dateECS?.isValid ? dateECS.toFormat('dd/MM/yyyy') : '';

        // Disable fields that have values, enable those without values
        // Fields to exclude from enabling/disabling logic
        const excludedFields = ['agent', 'typeRenouvellement', 'categoriePoste', 'identifiantAgent', 'grade', 'fonction'];

        Object.keys(this.form.controls).forEach((controlName) => {
          const control = this.form.get(controlName);
          if (control && !excludedFields.includes(controlName)) {
            if (control.value) {
              control.disable({ emitEvent: false });
            } else {
              control.enable({ emitEvent: false });
            }
          }
        });
      }

      // Load direction list if pole is available
      if (value.pole) {
        this.loadDirectionList(value.pole.id);
      }

      // Load service list if direction is available
      if (value.direction) {
        this.loadServiceList(value.direction.id);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    // Only check validation for enabled controls
    const enabledControlsValid = Object.keys(this.form.controls)
      .filter((key) => !this.form.get(key)?.disabled)
      .every((key) => this.form.get(key)?.valid);

    return enabledControlsValid ? null : { invalidSituationAdministrativeData: true };
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.disableForm() : this.enableForm();
  }

  disableForm(): void {
    Object.keys(this.form.controls).forEach((key) => {
      if (key !== 'agent' && key !== 'identifiantAgent' && key !== 'categoriePoste' && key !== 'grade' && key !== 'fonction') {
        this.form.get(key)?.disable({ emitEvent: false });
      }
    });
  }

  enableForm(): void {
    Object.keys(this.form.controls).forEach((key) => {
      if (key !== 'agent' && key !== 'identifiantAgent' && key !== 'categoriePoste' && key !== 'grade' && key !== 'fonction') {
        this.form.get(key)?.enable({ emitEvent: false });
      }
    });
  }

  // Load methods for different data types
  loadData(): void {
    forkJoin([
      this.referentielsService.getPole().pipe(take(1)),
      this.referentielsService.getPoste().pipe(take(1)),
      this.referentielsService.getTypeContrat().pipe(take(1)),
      this.referentielsService.getCategoriePoste().pipe(take(1)),
      this.referentielsService.getTypeRenouvellement().pipe(take(1))
    ]).subscribe(([poles, postes, contrats, categories, typeRecrutement]) => {
      if (poles) {
        this.poleList = poles.filter((pole: Pole) => pole.libelle?.trim());
      }
      if (postes) this.posteList = postes;
      if (contrats) this.typeContratList = contrats;
      if (categories) this.categoriePosteList = categories;
      if (typeRecrutement) {
        this.recrutementList = typeRecrutement.filter((type: any) => type.libelle && type.libelle.trim() !== '');
        this.recrutementListFiltered = [...this.recrutementList];
      }
      this.cdr.detectChanges();
    });
  }

  loadDirectionList(idPole: string): void {
    this.directionService
      .getDirectionsDemandesByPole(idPole)
      .pipe(take(1))
      .subscribe((data) => {
        if (data) {
          this.directionList = data;
          this.cdr.detectChanges();
        }
      });
  }

  loadServiceList(idDirection: string): void {
    this.fonctionnelService
      .getServicesDemandesByDirection(idDirection)
      .pipe(take(1))
      .subscribe((data) => {
        if (data) {
          this.serviceFonctionnelList = data.filter(
            (service: ServiceFonctionnel) => service.direction && service.direction.id === idDirection
          );
          this.cdr.detectChanges();
        }
      });
  }

  // Comparison functions for objects
  compareById(item1: any, item2: any): boolean {
    if (!item1 || !item2) {
      return false;
    }
    return item1.id === item2.id;
  }

  // Display methods
  displayService(service: ServiceFonctionnel): string {
    return service ? service.libelle : '';
  }

  // Type renouvellement methods
  filterRenouvellementList = (event: Event): void => {
    const value = (event.target as HTMLInputElement).value;
    if (typeof value !== 'string') {
      this.recrutementListFiltered = [...this.recrutementList];
      return;
    }

    const filterValue = value.toLowerCase();
    this.recrutementListFiltered = this.recrutementList.filter((type) => type.libelle.toLowerCase().includes(filterValue));
    this.cdr.detectChanges();
  };

  effaceFiltreTypeRen = (event: Event, controlName: string): void => {
    event.preventDefault();
    if (controlName) {
      this.form.get(controlName)?.setValue(null);
      this.recrutementListFiltered = [...this.recrutementList];
      this.cdr.detectChanges();
    }
  };

  typeRenouvellementdisplayFn = (type: any): string => {
    return type ? type.libelle : '';
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
